import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, from, map, Observable, throwError, timeout} from 'rxjs';
import {SCREENSHOT_SIZE} from '@q9elements/ui-kit/common';
import {environment} from 'src/environments/environment';
import {DIAGRAM_VERSION_STATES_ENUM} from 'src/app/editor-interface/shared/models/diagram-version.constant';

export interface ScreenshotData {
  url: string;
  meta: {
    name: string;
    state: string;
    mapName: string;
  };
}

export interface ScreenshotDataCell extends ScreenshotData {
  exp: Date;
  error: Error;
  errFlag: boolean;
  errorLoading: (err?: Error) => void;
}

interface LinkCache {
  [id: string]: {
    [state: string]: {
      _300x200?: ScreenshotDataCell;
      _480x320?: ScreenshotDataCell;
      _600x400?: ScreenshotDataCell;
    };
  };
}

/**
 * Image link caching & error handling
 **/

class Cache {
  private storage: LinkCache = {};

  public get(id: string, state: string, size: string): ScreenshotDataCell {
    if (this.storage[id] && this.storage[id][state] && this.storage[id][state][size]) {
      return this.storage[id][state][size];
    }
  }

  public set(
    id: string,
    state: string,
    size: string,
    exp: number,
    data: ScreenshotData
  ): ScreenshotDataCell {
    if (!this.storage[id]) {
      this.storage[id] = {};
    }

    if (!this.storage[id][state]) {
      this.storage[id][state] = {};
    }

    const expTime = new Date(Date.now() + exp * 1000);

    return (this.storage[id][state][size] = new CacheUnit(data, expTime));
  }

  public setError(
    id: string,
    state: string,
    size: string,
    error?: Error
  ): ScreenshotDataCell | void {
    if (!this.storage[id]) {
      return;
    }

    if (!this.storage[id][state]) {
      return;
    }

    const unit = this.storage[id][state][size];

    if (unit instanceof CacheUnit) {
      unit.errorLoading(error);
    }

    return unit;
  }
}

class CacheUnit implements ScreenshotDataCell {
  private _url: string;
  public error: Error;
  public errFlag = false;
  public meta: any;

  constructor(
    data: ScreenshotData,
    public exp: Date
  ) {
    if (!data || !data.url) {
      throw new TypeError("First argument is null or missing 'url' key");
    }

    this.url = data.url;
    this.meta = data.meta;
  }

  public get url() {
    return this._url;
  }

  public set url(data) {
    if (typeof data === 'string') {
      this._url = data;
      this.errFlag = false;
    } else {
      throw new TypeError("Url must be of 'string' type");
    }
  }

  public errorLoading(err?: Error) {
    console.log('err loading');
    this.errFlag = true;

    if (err) {
      this.error = err;
    }
  }
}

/**
 * Main service
 **/

@Injectable({
  providedIn: 'root'
})
export class ScreenshotService {
  constructor(private http: HttpClient) {}

  private APIURL = environment.API_ENDPOINT;
  private DefaultSize: string = SCREENSHOT_SIZE.SMALL;
  private DefaultState = DIAGRAM_VERSION_STATES_ENUM.DRAFT;
  private DefaultExpiryTime = 120;

  private cache = new Cache();

  private getScreenshotUrl(id: string, state: string, size: string, exp: number) {
    const options = {
      headers: new HttpHeaders().set('Authorization', 'token'),
      params: new HttpParams().set('size', size).set('exp', String(exp))
    };

    return this.http.get(
      `${this.APIURL}/diagram/${id}/state/${state}/screenshot`,
      options
    ) as Observable<ScreenshotData>;
  }

  public getScreenshot(
    dvId: string,
    state = this.DefaultState,
    size = this.DefaultSize,
    exp = this.DefaultExpiryTime
  ): Observable<ScreenshotDataCell> {
    const cached = this.cache.get(dvId, state, size);

    if (cached && new Date() < cached.exp && cached.errFlag === false) {
      return from([cached]);
    } else {
      return this.getScreenshotUrl(dvId, state, size, exp).pipe(
        map((data: ScreenshotData) => this.cache.set(dvId, state, size, exp, data)),
        timeout(5000),
        catchError((err, obs) => {
          this.cache.setError(dvId, state, size, err);

          return throwError(err);
        })
      );
    }
  }
}
